import * as React from 'react';
import { Entypo } from '~/components/core/Icons';
import { scaleSize } from '~/constants/scale';
import { Pressable, StyleSheet, View } from 'react-native';
import { goBack } from '~/navigation';
import { useStyles } from '~/hooks';
import { Theme } from '~/theme';
import { Text } from '../core';
import { FONT_SIZE_L, FONT_SIZE_M } from '~/constants';

type Props = {
    title: string;
    hideBackButton?: boolean;
    // `title` is already-resolved text, not an i18n key (the AI Strategy screens pass a step name
    // or a strategy summary). Left off, `title` is translated as before.
    noTranslateTitle?: boolean;
    // One-line, truncating title — for long titles like a strategy summary. Off => wraps as before.
    titleNumberOfLines?: number;
    // Actions pinned to the right edge (e.g. the list's info/filter/create, the detail's "...").
    right?: React.ReactNode;
    // Overrides the default global goBack (the detail routes back to the list explicitly).
    onBack?: () => void;
    // Chevron only, no "back" word — buys horizontal room for a long left-aligned title.
    hideBackLabel?: boolean;
    // 'left' hands the title the full width between the chevron and the actions so a longer line
    // (e.g. the detail's TP/SL) fits; the default 'center' keeps the Settings-style centred title.
    titleAlign?: 'center' | 'left';
    // Shrinks the title (the detail uses a smaller size so the full TP/SL line clears the actions).
    titleFontSize?: number;
};

export default function CustomHeader({
    title,
    hideBackButton,
    noTranslateTitle,
    titleNumberOfLines,
    right,
    onBack,
    hideBackLabel,
    titleAlign = 'center',
    titleFontSize,
}: Props) {
    const styles = useStyles(stylesGenerator);
    const left = titleAlign === 'left';

    const back = hideBackButton ? (
        left ? null : (
            <View style={styles.backContainer} />
        )
    ) : (
        <Pressable
            onPress={onBack ?? (() => goBack())}
            style={left ? styles.backLeft : styles.backContainer}
            hitSlop={scaleSize(10)}
            accessibilityLabel="back"
        >
            <Entypo size={scaleSize(24)} name="chevron-left" style={styles.backArrow} />
            {hideBackLabel ? null : (
                <Text fontType="LATO_REGULAR" style={styles.backText}>
                    {'back'}
                </Text>
            )}
        </Pressable>
    );

    const titleNode = (
        <Text
            fontType="LATO_BOLD"
            style={[styles.headerText, titleFontSize ? { fontSize: titleFontSize } : null]}
            noTranslate={noTranslateTitle}
            numberOfLines={titleNumberOfLines}
        >
            {title}
        </Text>
    );

    // Left-aligned: an in-flow flex row so the title takes the room between the chevron and the
    // actions. Centred (default): the title sits screen-centred in a fixed 70% box with the actions
    // overlaid on the right edge — unchanged, so Settings and the rest of the app look the same.
    if (left) {
        return (
            <View style={styles.container}>
                {back}
                <View style={styles.titleLeft}>{titleNode}</View>
                {right ? <View style={styles.rightInline}>{right}</View> : null}
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {back}
            <View style={styles.headerTitle}>{titleNode}</View>
            {right ? <View style={styles.rightContainer}>{right}</View> : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flexDirection: 'row',
            width: '100%',
            paddingHorizontal: scaleSize(5),
            paddingBottom: scaleSize(5),
            paddingTop: scaleSize(5),
            backgroundColor: theme.backgroundSecondary,
        },
        backArrow: { color: theme.tabIconDefault },
        backContainer: {
            width: '15%',
            height: scaleSize(28),
            paddingLeft: scaleSize(2),
            justifyContent: 'flex-start',
            alignItems: 'center',
            flexDirection: 'row',
        },
        headerTitle: { width: '70%', justifyContent: 'center', alignItems: 'center' },
        headerText: { color: theme.tabIconDefault, fontSize: FONT_SIZE_L },
        backText: { color: theme.tabIconDefault, fontSize: FONT_SIZE_M, paddingBottom: scaleSize(2) },
        // Overlaid on the right edge so the back/title flex row (and thus title centring) is
        // untouched for the title-only screens that pass no `right`.
        rightContainer: {
            position: 'absolute',
            right: scaleSize(8),
            top: 0,
            bottom: 0,
            flexDirection: 'row',
            alignItems: 'center',
        },
        // Left-aligned variant: chevron hugs the left, title flexes to fill, actions sit in-flow
        // after it so the title truncates against them instead of overrunning.
        backLeft: {
            height: scaleSize(28),
            paddingLeft: scaleSize(2),
            paddingRight: scaleSize(4),
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
        },
        // paddingLeft lands the title at ~14px from the edge — aligned with the list rows below
        // (their paddingLeft 14), and a comfortable gap after the detail's chevron.
        titleLeft: { flex: 1, height: scaleSize(28), justifyContent: 'center', paddingLeft: scaleSize(9) },
        rightInline: {
            height: scaleSize(28),
            marginLeft: scaleSize(4),
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
        },
    });
